/**
 * Duel wire protocol — the WebSocket contract between the SPA and the
 * DuelRoom Durable Object. It replaces the prototype's BroadcastChannel bus
 * message for message, with two additions the broadcast bus could not express:
 * a distinction between client and server messages, and reconnection.
 */
import type { DeckId, Entry } from "./vocab";

/**
 * Bumped whenever a message shape changes. The DO rejects a mismatch outright
 * rather than letting a gradual deployment desynchronise a live duel.
 */
export const PROTOCOL_VERSION = 3;

/** No I, L or O — they are unreadable in a code the player reads aloud. */
export const CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ";
export const CODE_LENGTH = 4;

export const WORD_COUNT_OPTIONS = [5, 10, 15, 20] as const;
export type WordCount = (typeof WORD_COUNT_OPTIONS)[number];

export type Side = "host" | "guest";

export function isWordCount(n: unknown): n is WordCount {
  return typeof n === "number" && (WORD_COUNT_OPTIONS as readonly number[]).includes(n);
}

export function isRoomCode(s: unknown): s is string {
  return (
    typeof s === "string" &&
    s.length === CODE_LENGTH &&
    [...s].every((c) => CODE_ALPHABET.includes(c))
  );
}

/* ── Client -> Durable Object ─────────────────────────────────────────── */

export type ClientMsg =
  /**
   * First message on every connection. Identity, not a greeting.
   * `deckId` is this player's own prompt language — the room deals one set of
   * cards and each side reads it in the language it asked for, so a French
   * speaker and a Korean speaker can duel on the same words.
   */
  | { t: "hello"; v: number; playerId: string; name: string; deckId: DeckId }
  /** After each word. Replaces the prototype's `p`. */
  | { t: "progress"; done: number; score: number }
  /** Deck exhausted. Replaces the prototype's `f`. */
  | { t: "finish"; score: number; ms: number }
  /** Host only. Same word count, fresh deck. */
  | { t: "rematch" }
  /** Explicit leave, for immediacy. Socket close is the authoritative signal. */
  | { t: "leave" };

/* ── Durable Object -> client ─────────────────────────────────────────── */

export type PlayerState = {
  name: string;
  score: number;
  done: number;
  ms: number;
  finished: boolean;
  present: boolean;
};

export type RoomSnapshot = {
  phase: "lobby" | "playing" | "over";
  code: string;
  deckId: DeckId;
  wordCount: number;
  /** Empty while the room is still in the lobby. */
  deck: Entry[];
  startAt: number;
  side: Side;
  you: PlayerState;
  opponent: PlayerState;
  /** Set when the opponent disconnected mid-duel. */
  oppLeft: boolean;
};

export type ServerMsg =
  /** No such room: nobody ever created that code, or it expired. */
  | { t: "room-missing" }
  /** Two players are already connected. */
  | { t: "room-full" }
  | { t: "version-mismatch"; expected: number }
  /**
   * You are alone in the room. `side` matters: the host is waiting for an
   * opponent, a guest is waiting for a host who has not connected yet.
   */
  | { t: "waiting"; code: string; side: Side }
  /**
   * Both players in. `startAt` is stamped by the DO so the clock is shared.
   * `deck` and `deckId` are per recipient: the same cards, rendered in the
   * language that player asked for.
   */
  | { t: "start"; deck: Entry[]; deckId: DeckId; opponent: string; startAt: number }
  | { t: "opp-progress"; done: number; score: number }
  | { t: "opp-finish"; score: number; ms: number }
  /** The opponent's socket dropped. The duel carries on; they may come back. */
  | { t: "opp-away" }
  /** They came back. */
  | { t: "opp-back" }
  /**
   * The duel is over by forfeit: the opponent stayed away past the grace
   * period. This is the only departure that ends a duel — a dropped socket on
   * its own never does.
   */
  | { t: "opp-left" }
  /** Full room state, sent on every (re)connection into a running duel. */
  | { t: "state"; snapshot: RoomSnapshot };

/**
 * How long a player may be disconnected before the duel is awarded to whoever
 * is still there. Long enough to survive a tunnel, a lock screen or a Wi-Fi
 * handover; short enough that the other player is not stranded.
 */
export const FORFEIT_GRACE_MS = 2 * 60_000;

/** Close codes the DO uses, so the client can tell why it was dropped. */
export const CLOSE = {
  ROOM_FULL: 4001,
  EXPIRED: 4002,
  VERSION_MISMATCH: 4003,
} as const;

/* ── Result ───────────────────────────────────────────────────────────── */

export type DuelOutcome = "win" | "loss" | "draw" | "win-speed" | "loss-speed" | "win-forfeit";

/**
 * Higher score wins; a tie goes to the lower elapsed time; still equal is a
 * draw. An opponent who left forfeits. Identical to the prototype's rule.
 */
export function outcome(
  myScore: number,
  oppScore: number,
  myMs: number,
  oppMs: number,
  oppLeft: boolean,
): DuelOutcome {
  if (oppLeft) return "win-forfeit";
  if (myScore > oppScore) return "win";
  if (myScore < oppScore) return "loss";
  if (myMs && oppMs && myMs !== oppMs) return myMs < oppMs ? "win-speed" : "loss-speed";
  return "draw";
}
